import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  PDFViewer,
  Image,
} from "@react-pdf/renderer";
import image from "../../../assets/img/marifer.png";

const styles = StyleSheet.create({
  viewer: { width: "100%", height: "75vh" },
  page: { padding: 30, fontFamily: "Helvetica", backgroundColor: "#fff" },

  // Header
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    paddingBottom: 12,
    borderBottom: "2 solid #f43f5e",
  },
  logo: { width: 70, height: 70, borderRadius: 8 },
  headerRight: { alignItems: "flex-end" },
  title: { fontSize: 20, fontFamily: "Helvetica-Bold", color: "#be185d", letterSpacing: 1 },
  subtitle: { fontSize: 9, color: "#9ca3af", marginTop: 2 },
  date: { fontSize: 8, color: "#6b7280", marginTop: 4 },

  // Summary row
  summaryRow: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 16,
  },
  summaryBox: {
    flex: 1,
    padding: 10,
    borderRadius: 6,
    backgroundColor: "#fff0f6",
    border: "1 solid #fce7f3",
    alignItems: "center",
  },
  summaryLabel: { fontSize: 8, color: "#be185d", textTransform: "uppercase", letterSpacing: 0.5 },
  summaryValue: { fontSize: 16, fontFamily: "Helvetica-Bold", color: "#f43f5e", marginTop: 2 },

  // Table
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f43f5e",
    borderRadius: 4,
    paddingVertical: 7,
    paddingHorizontal: 8,
    marginBottom: 2,
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 7,
    paddingHorizontal: 8,
    borderBottom: "1 solid #fce7f3",
  },
  tableRowAlt: {
    flexDirection: "row",
    paddingVertical: 7,
    paddingHorizontal: 8,
    borderBottom: "1 solid #fce7f3",
    backgroundColor: "#fff5f7",
  },
  colName:   { flex: 2.5, fontSize: 9 },
  colCode:   { flex: 1.5, fontSize: 9, textAlign: "center" },
  colSku:    { flex: 1.5, fontSize: 9, textAlign: "center" },
  colAttr:   { flex: 3, fontSize: 9 },
  colStack:  { flex: 1.2, fontSize: 9, textAlign: "right" },
  colPrice:  { flex: 1.2, fontSize: 9, textAlign: "right" },
  colStatus: { flex: 1.2, fontSize: 9, textAlign: "center" },

  colNameH:   { flex: 2.5, fontSize: 9, color: "#fff", fontFamily: "Helvetica-Bold" },
  colCodeH:   { flex: 1.5, fontSize: 9, color: "#fff", fontFamily: "Helvetica-Bold", textAlign: "center" },
  colSkuH:    { flex: 1.5, fontSize: 9, color: "#fff", fontFamily: "Helvetica-Bold", textAlign: "center" },
  colAttrH:   { flex: 3, fontSize: 9, color: "#fff", fontFamily: "Helvetica-Bold" },
  colStackH:  { flex: 1.2, fontSize: 9, color: "#fff", fontFamily: "Helvetica-Bold", textAlign: "right" },
  colPriceH:  { flex: 1.2, fontSize: 9, color: "#fff", fontFamily: "Helvetica-Bold", textAlign: "right" },
  colStatusH: { flex: 1.2, fontSize: 9, color: "#fff", fontFamily: "Helvetica-Bold", textAlign: "center" },

  statusOk:    { color: "#16a34a", fontFamily: "Helvetica-Bold" },
  statusLow:   { color: "#ea580c", fontFamily: "Helvetica-Bold" },
  statusEmpty: { color: "#dc2626", fontFamily: "Helvetica-Bold" },

  footer: {
    position: "absolute",
    bottom: 20,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    borderTop: "1 solid #fce7f3",
    paddingTop: 6,
  },
  footerText: { fontSize: 7, color: "#9ca3af" },
});

const getStatus = (stack) => {
  if (stack <= 0) return { label: "AGOTADO", style: styles.statusEmpty };
  if (stack < 10) return { label: "BAJO", style: styles.statusLow };
  return { label: "OK", style: styles.statusOk };
};

const StackInsumosPDF = ({ variants = [] }) => {
  const now = new Date().toLocaleString("es-MX");
  const total = variants.length;
  const agotados = variants.filter((v) => parseFloat(v.stock ?? 0) <= 0).length;
  const bajos = variants.filter((v) => { const s = parseFloat(v.stock ?? 0); return s > 0 && s < 10; }).length;

  return (
    <PDFViewer style={styles.viewer}>
      <Document title="Reporte Inventario Insumos">
        <Page size="A4" orientation="landscape" style={styles.page}>
          {/* Header */}
          <View style={styles.header}>
            <Image src={image} style={styles.logo} />
            <View style={styles.headerRight}>
              <Text style={styles.title}>INVENTARIO DE INSUMOS</Text>
              <Text style={styles.subtitle}>Reporte de stock actual</Text>
              <Text style={styles.date}>Generado: {now}</Text>
            </View>
          </View>

          {/* Summary */}
          <View style={styles.summaryRow}>
            <View style={styles.summaryBox}>
              <Text style={styles.summaryLabel}>Total variantes</Text>
              <Text style={styles.summaryValue}>{total}</Text>
            </View>
            <View style={[styles.summaryBox, { backgroundColor: "#fff1f2", border: "1 solid #fecdd3" }]}>
              <Text style={[styles.summaryLabel, { color: "#dc2626" }]}>Agotados</Text>
              <Text style={[styles.summaryValue, { color: "#dc2626" }]}>{agotados}</Text>
            </View>
            <View style={[styles.summaryBox, { backgroundColor: "#fff7ed", border: "1 solid #fed7aa" }]}>
              <Text style={[styles.summaryLabel, { color: "#ea580c" }]}>Stock bajo</Text>
              <Text style={[styles.summaryValue, { color: "#ea580c" }]}>{bajos}</Text>
            </View>
            <View style={[styles.summaryBox, { backgroundColor: "#f0fdf4", border: "1 solid #bbf7d0" }]}>
              <Text style={[styles.summaryLabel, { color: "#16a34a" }]}>Disponibles</Text>
              <Text style={[styles.summaryValue, { color: "#16a34a" }]}>{total - agotados}</Text>
            </View>
          </View>

          {/* Table header */}
          <View style={styles.tableHeader}>
            <Text style={styles.colNameH}>Insumo</Text>
            <Text style={styles.colCodeH}>Código</Text>
            <Text style={styles.colSkuH}>SKU</Text>
            <Text style={styles.colAttrH}>Atributos</Text>
            <Text style={styles.colStackH}>Stock</Text>
            <Text style={styles.colPriceH}>Precio</Text>
            <Text style={styles.colStatusH}>Estado</Text>
          </View>

          {/* Rows */}
          {variants.map((v, idx) => {
            const stock = parseFloat(v.stock ?? 0);
            const status = getStatus(stock);
            const attrs = (v.attributes || []).map((a) => `${a.attributeName}: ${a.attributeValue}`).join(" | ");
            const RowStyle = idx % 2 === 0 ? styles.tableRow : styles.tableRowAlt;
            return (
              <View key={v.id} style={RowStyle}>
                <Text style={styles.colName}>{v.insumo?.nombre || ""}</Text>
                <Text style={styles.colCode}>{v.insumo?.codigo || "-"}</Text>
                <Text style={styles.colSku}>{v.sku || "-"}</Text>
                <Text style={styles.colAttr}>{attrs || "-"}</Text>
                <Text style={styles.colStack}>{stock}</Text>
                <Text style={styles.colPrice}>{v.price != null ? `$${parseFloat(v.price).toFixed(2)}` : "-"}</Text>
                <Text style={[styles.colStatus, status.style]}>{status.label}</Text>
              </View>
            );
          })}

          {/* Footer */}
          <View style={styles.footer} fixed>
            <Text style={styles.footerText}>Boopsi — Sistema de Gestión</Text>
            <Text style={styles.footerText}>{now}</Text>
          </View>
        </Page>
      </Document>
    </PDFViewer>
  );
};

export default StackInsumosPDF;
